import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import {
  ShieldCheck, Lock, Database, Server, Globe2, KeyRound, EyeOff, FileCheck2,
  UserX, ClipboardCheck, Fingerprint, Sparkles, ArrowRight, MailCheck,
} from "lucide-react";

const PILLARS = [
  {
    icon: EyeOff,
    tag: "MODEL CALLS",
    title: "Zero-retention model calls",
    desc: "Every call to Claude Sonnet 4.5 runs under Anthropic's zero-retention API terms. Your prompts, drafts, and outputs are not stored on the model provider's side after the call completes. Nothing you write is used to train third-party foundation models.",
  },
  {
    icon: UserX,
    tag: "CHUCK TRAINING",
    title: "Chuck training is opt-in, de-identified, Enterprise-excluded",
    desc: "Qualmitta's in-house model, Chuck, is trained on expert-graded regulatory content — never on your raw submissions. Training is off by default for individual accounts, and Enterprise tenants are excluded from training pipelines unless a program owner explicitly opts in.",
  },
  {
    icon: Database,
    tag: "TENANT ISOLATION",
    title: "Per-tenant data isolation",
    desc: "Every project, draft, predicate set, risk item, chat session, and telemetry event is scoped to its owner's user_id at the row level. Team invites grant access explicitly. There is no shared 'global' tenant table.",
  },
  {
    icon: Server,
    tag: "DATA RESIDENCY",
    title: "Where your data lives",
    desc: "Application data (projects, drafts, chats, team assignments) is stored in a US-hosted MongoDB cluster managed by the Emergent platform. External calls to openFDA, USPTO ODP, ClinicalTrials.gov and eCFR are read-only and stateless — Qualmitta never sends your device or therapeutic data to those services.",
  },
  {
    icon: KeyRound,
    tag: "SECRETS & KEYS",
    title: "Encrypted secrets, rotated regularly",
    desc: "API keys (Anthropic, Stripe, Resend, USPTO) live only in encrypted server environment variables. They are never exposed to the browser, never logged, and are rotated on a defined schedule.",
  },
  {
    icon: ClipboardCheck,
    tag: "AUDIT TRAIL",
    title: "Every artifact is versioned",
    desc: "Documents have version history and redlines. Predicate sets, risk items, and QMS SOPs are timestamped on create + update. Admins can pull the export log on request as part of an Enterprise agreement.",
  },
];

const DATA_FLOWS = [
  { role: "SUBMISSION DRAFTING", desc: "Your inputs → Claude Sonnet 4.5 → streamed markdown → stored in your tenant. Zero retention on the model side." },
  { role: "REGULATORY CHAT",     desc: "Your questions + optional project scope → Claude → streamed answers → chat history stored in your tenant." },
  { role: "PREDICATE / RLD",     desc: "Your query → openFDA public API (read-only) → ranked results stored in your predicate sets." },
  { role: "PATENTS / FTO",       desc: "Your device features → USPTO ODP public API (read-only) → screening report stored in your project." },
  { role: "RISK MATRIX",         desc: "Your hazards → stored in your tenant. AI 'suggest' calls the model with your project meta only — never with prior projects or shared examples." },
  { role: "QMS SOP",             desc: "SOP template + company name → Claude → streamed SOP → your download. Nothing shared with other tenants." },
];

const RIGHTS = [
  { title: "Right to access",   desc: "Email hello@qualmitta.com and we return every artifact tied to your user_id within 5 business days." },
  { title: "Right to deletion", desc: "Deletion of your account and every associated project, draft, chat and telemetry event is honored within 30 days." },
  { title: "Right to portability", desc: "Every artifact is exportable to PDF, DOCX, Markdown, or CSV directly from the workspace." },
  { title: "Right to correction",  desc: "Contact us to correct any account metadata (name, email, org)." },
];

const CERT_STATUS = [
  { name: "SOC 2 Type II",       status: "Emergent-managed infra (in progress)", desc: "Underlying compute and storage inherit Emergent's operational controls." },
  { name: "HIPAA",               status: "Not yet — no PHI accepted",              desc: "Qualmitta does not ingest identifiable patient health information today. If your workflow needs HIPAA, contact us for the Enterprise path." },
  { name: "GDPR / CCPA",         status: "Compliant with individual rights",      desc: "Access, deletion, portability, and correction rights are honored end-to-end." },
  { name: "21 CFR Part 11",      status: "Roadmap — audit trail already in place","desc": "Version history and timestamped signatures live today. Full electronic-record + electronic-signature compliance is on the Enterprise roadmap." },
];

export default function Security() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="border-b border-slate-200 grain relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-28">
          <div className="mb-6 inline-flex items-center gap-3 border border-slate-300 bg-white px-3 py-1.5">
            <ShieldCheck className="h-4 w-4 text-[#2563EB]" />
            <span className="overline text-slate-600">TRUST · SECURITY · DATA HANDLING</span>
          </div>
          <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-tight lg:text-7xl">
            Your regulatory work
            <br />
            <span className="italic text-slate-600">stays yours.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
            Qualmitta is used by regulatory affairs, quality, and R&amp;D teams working on
            confidential device and drug programs. This page describes how we handle your data,
            where it lives, what the AI models see, and what rights you have.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-24">
          <div className="mb-12 max-w-2xl">
            <div className="overline mb-3 text-[#2563EB]">HOW WE HANDLE YOUR DATA</div>
            <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
              Six commitments,
              <br />
              <span className="italic text-slate-600">in plain language.</span>
            </h2>
          </div>
          <div className="grid gap-px bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p) => {
              const I = p.icon;
              return (
                <div key={p.tag} data-testid="security-pillar" className="bg-white p-8">
                  <I className="mb-5 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="overline mb-2 text-[#2563EB]">{p.tag}</div>
                  <h3 className="font-serif text-xl font-semibold leading-tight text-slate-900">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data flows */}
      <section className="border-b border-slate-200 bg-[#FAFAF7]">
        <div className="mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="overline mb-3 text-[#2563EB]">DATA FLOWS</div>
              <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
                What happens
                <br />
                <span className="italic text-slate-600">every time you click.</span>
              </h2>
              <p className="mt-5 text-slate-600">
                We label every AI-powered feature by exactly which model call is triggered, what
                data leaves your tenant, and where the result lands.
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="border border-slate-200 bg-white">
                {DATA_FLOWS.map((f) => (
                  <div key={f.role} data-testid="security-flow" className="grid grid-cols-1 gap-3 border-b border-slate-100 p-5 last:border-b-0 md:grid-cols-3">
                    <div className="overline text-[#2563EB]">{f.role}</div>
                    <div className="md:col-span-2 text-sm text-slate-700">{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rights */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="overline mb-3 text-[#2563EB]">YOUR RIGHTS</div>
              <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
                Access. Delete.
                <br />
                <span className="italic text-slate-600">Export. Correct.</span>
              </h2>
              <p className="mt-5 text-slate-600">
                Under GDPR, CCPA, and our own principles — you are always in control of the
                data you put into Qualmitta.
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="grid gap-px bg-slate-200 md:grid-cols-2">
                {RIGHTS.map((r) => (
                  <div key={r.title} data-testid="security-right" className="bg-white p-6">
                    <div className="mb-2 flex items-center gap-2">
                      <Fingerprint className="h-4 w-4 text-[#2563EB]" />
                      <div className="font-serif text-lg font-semibold text-slate-900">{r.title}</div>
                    </div>
                    <p className="text-sm text-slate-600">{r.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="border-b border-slate-200 bg-[#FAFAF7]">
        <div className="mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-24">
          <div className="mb-10 max-w-2xl">
            <div className="overline mb-3 text-[#2563EB]">CERTIFICATIONS &amp; POSTURE</div>
            <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
              Where we are today,
              <br />
              <span className="italic text-slate-600">and what's next.</span>
            </h2>
          </div>
          <div className="border border-slate-200 bg-white">
            <div className="hidden border-b border-slate-200 bg-slate-50 px-5 py-2 md:grid md:grid-cols-3">
              <div className="overline text-slate-500">STANDARD</div>
              <div className="overline text-slate-500">STATUS</div>
              <div className="overline text-slate-500">NOTE</div>
            </div>
            {CERT_STATUS.map((c) => (
              <div key={c.name} data-testid="security-cert" className="grid grid-cols-1 gap-2 border-b border-slate-100 p-5 last:border-b-0 md:grid-cols-3">
                <div>
                  <div className="font-mono text-sm font-semibold text-slate-900">{c.name}</div>
                </div>
                <div className="text-sm text-slate-700">{c.status}</div>
                <div className="text-sm text-slate-500">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1200px] flex-col items-start justify-between gap-8 px-6 py-16 lg:flex-row lg:items-center lg:px-10">
          <div>
            <div className="overline mb-2 text-[#2563EB]">ENTERPRISE PROCUREMENT</div>
            <h3 className="font-serif text-3xl font-semibold text-slate-900 lg:text-4xl">
              Need a data-processing agreement or a security questionnaire?
            </h3>
            <p className="mt-2 max-w-2xl text-slate-600">
              We share our current DPA, subprocessor list, and vendor security questionnaire on
              request. Reach out and we'll turn it around within one business day.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="mailto:hello@qualmitta.com?subject=Security%20%26%20DPA%20request"
              data-testid="security-cta-email"
              className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8]"
            >
              <MailCheck className="h-4 w-4" /> Email security team
            </a>
            <Link
              to="/contact"
              data-testid="security-cta-contact"
              className="inline-flex items-center gap-2 border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB]"
            >
              Contact form <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#FAFAF7]">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-8 lg:px-10">
          <div className="overline text-slate-500">
            © Qualmitta · Not legal advice · Not medical advice
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="overline text-slate-500 hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="overline text-slate-500 hover:text-slate-900">Terms</Link>
            <Link to="/disclaimer" className="overline text-slate-500 hover:text-slate-900">Disclaimer</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
